import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, Loader2, RotateCcw, DollarSign, Building2, ShieldCheck, Calculator } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { PageHeader } from '@/components/PageHeader';
import api from '@/lib/api';

const PROFILE_FIELDS = [
    { key: 'labor_per_square', label: 'Labor (per square / 100 sqft)', prefix: '$', step: 5, hint: 'Wage + benefits per 100 sqft of installed roof.' },
    { key: 'shingle_per_square', label: 'Shingles (per square)', prefix: '$', step: 5, hint: 'Architectural shingle materials cost.' },
    { key: 'underlayment_per_square', label: 'Underlayment (per square)', prefix: '$', step: 1 },
    { key: 'ice_water_per_roll', label: 'Ice & Water shield (per roll)', prefix: '$', step: 5 },
    { key: 'decking_per_sheet', label: 'Decking (4x8 sheet, all-in)', prefix: '$', step: 5 },
    { key: 'dump_fee', label: 'Tear-off / dumpster fee', prefix: '$', step: 25 },
    { key: 'permit_fee', label: 'Default city permit fee', prefix: '$', step: 25 },
    { key: 'waste_pct', label: 'Material waste %', suffix: '%', step: 1, max: 50 },
    { key: 'margin_pct', label: 'Target gross margin %', suffix: '%', step: 1, max: 80 },
];

const STORY_KEYS = [
    { key: '1', label: '1 story', hint: 'Baseline. No multiplier.' },
    { key: '2', label: '2 stories', hint: 'Adds rigging + harness time.' },
    { key: '3', label: '3+ stories', hint: 'Lifts / additional safety.' },
];

const RANGE_FIELDS = [
    { key: 'range_low_pct', label: 'Range low multiplier', hint: 'e.g. 0.95 = 5% below total', min: 0.5, max: 1.0, step: 0.01 },
    { key: 'range_high_pct', label: 'Range high multiplier', hint: 'e.g. 1.10 = 10% above total', min: 1.0, max: 2.0, step: 0.01 },
];

export default function SettingsPage() {
    const [s, setS] = useState({
        company_name: '', legal_name: '', address: '', phone: '', email: '',
        license_number: '', license_state: '', liability_insurance: '', workers_comp: '', default_warranty: '',
    });
    const [profile, setProfile] = useState(null);
    const [defaults, setDefaults] = useState(null);
    const [hasCustomProfile, setHasCustomProfile] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savingProfile, setSavingProfile] = useState(false);
    const [resettingProfile, setResettingProfile] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [settingsRes, profileRes] = await Promise.all([
                    api.get('/me/settings').catch(() => ({ data: {} })),
                    api.get('/me/pricing-profile').catch(() => ({ data: {} })),
                ]);
                setS((prev) => ({ ...prev, ...(settingsRes.data || {}) }));
                if (profileRes.data?.profile) {
                    setProfile(profileRes.data.profile);
                    setDefaults(profileRes.data.defaults);
                    setHasCustomProfile(!!profileRes.data.has_custom);
                }
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const set = (k) => (e) => setS({ ...s, [k]: e?.target ? e.target.value : e });
    const setProfileKey = (k) => (e) => {
        const raw = e?.target ? e.target.value : e;
        setProfile((p) => ({ ...(p || {}), [k]: raw === '' ? null : Number(raw) }));
    };
    const setStoryMult = (k) => (e) => {
        const raw = e?.target ? e.target.value : e;
        setProfile((p) => ({
            ...(p || {}),
            story_multipliers: { ...((p || {}).story_multipliers || {}), [k]: raw === '' ? null : Number(raw) },
        }));
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.patch('/me/settings', s);
            toast.success('Settings saved');
        } catch (e) {
            toast.error('Save failed');
        } finally {
            setSaving(false);
        }
    };

    const saveProfile = async () => {
        if (!profile) return;
        setSavingProfile(true);
        try {
            // Strip null story-multiplier entries
            const sm = { ...(profile.story_multipliers || {}) };
            Object.keys(sm).forEach((k) => { if (sm[k] === null || sm[k] === undefined || Number.isNaN(sm[k])) delete sm[k]; });
            const payload = { ...profile, story_multipliers: Object.keys(sm).length ? sm : undefined };
            // Drop null fields so backend keeps existing
            Object.keys(payload).forEach((k) => { if (payload[k] === null || payload[k] === undefined) delete payload[k]; });
            const r = await api.put('/me/pricing-profile', payload);
            setProfile(r.data.profile);
            setHasCustomProfile(!!r.data.has_custom);
            toast.success('Pricing profile saved');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Save failed');
        } finally {
            setSavingProfile(false);
        }
    };

    const resetProfile = async () => {
        setResettingProfile(true);
        try {
            const r = await api.delete('/me/pricing-profile');
            setProfile(r.data.profile);
            setHasCustomProfile(false);
            toast.success('Pricing profile reset to industry defaults');
        } catch (e) {
            toast.error('Reset failed');
        } finally {
            setResettingProfile(false);
        }
    };

    if (loading) return <div className="text-slate-500">Loading…</div>;

    return (
        <>
            <PageHeader
                title="Company Settings"
                subtitle="Defaults auto-fill on every new quote."
                testId="settings-header"
                actions={
                    <Button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="settings-save-button">
                        {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save
                    </Button>
                }
            />

            <Card>
                <CardHeader>
                    <CardTitle className="font-display flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-700" /> Company info</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Item label="Trading name" v={s.company_name} onChange={set('company_name')} testId="settings-company-name" />
                    <Item label="Legal name (exact)" v={s.legal_name} onChange={set('legal_name')} testId="settings-legal-name" />
                    <Item label="Address" v={s.address} onChange={set('address')} className="md:col-span-2" testId="settings-address" />
                    <Item label="Phone" v={s.phone} onChange={set('phone')} testId="settings-phone" />
                    <Item label="Contact email" v={s.email} onChange={set('email')} testId="settings-email" />
                </CardContent>
            </Card>

            <Card className="mt-4">
                <CardHeader>
                    <CardTitle className="font-display flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-slate-700" /> License &amp; Insurance</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Item label="License number" v={s.license_number} onChange={set('license_number')} testId="settings-license-number" />
                    <Item label="License state" v={s.license_state} onChange={set('license_state')} testId="settings-license-state" />
                    <Item label="Liability insurance details" v={s.liability_insurance} onChange={set('liability_insurance')} className="md:col-span-2" testId="settings-liability" />
                    <Item label="Workers Comp details" v={s.workers_comp} onChange={set('workers_comp')} className="md:col-span-2" testId="settings-wc" />
                </CardContent>
            </Card>

            <Card className="mt-4">
                <CardHeader>
                    <CardTitle className="font-display">Default warranty text</CardTitle>
                </CardHeader>
                <CardContent>
                    <Label>Default workmanship warranty</Label>
                    <Textarea rows={3} value={s.default_warranty || ''} onChange={set('default_warranty')} data-testid="settings-default-warranty" />
                    <p className="text-xs text-slate-500 mt-2">Used as the starting point for new quotes.</p>
                </CardContent>
            </Card>

            {/* ───────────────────── Pricing Profile ───────────────────── */}
            {profile && (
                <Card className="mt-4 border-amber-200" data-testid="pricing-profile-card">
                    <CardHeader>
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <CardTitle className="font-display flex items-center gap-2">
                                <Calculator className="h-4 w-4 text-amber-600" /> Pricing Profile
                                {hasCustomProfile ? (
                                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 font-normal" data-testid="pricing-profile-custom-badge">
                                        Custom
                                    </Badge>
                                ) : (
                                    <Badge variant="outline" className="bg-slate-100 text-slate-700 font-normal" data-testid="pricing-profile-defaults-badge">
                                        Using industry defaults
                                    </Badge>
                                )}
                            </CardTitle>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={resetProfile}
                                    disabled={resettingProfile || !hasCustomProfile}
                                    data-testid="pricing-profile-reset"
                                >
                                    {resettingProfile ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1" />} Reset defaults
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={saveProfile}
                                    disabled={savingProfile}
                                    className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
                                    data-testid="pricing-profile-save"
                                >
                                    {savingProfile ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />} Save profile
                                </Button>
                            </div>
                        </div>
                        <p className="text-sm text-slate-500 pt-1">
                            Your unit costs and margins. These pre-fill the QuoteBuilder so the AI math matches your real economics.
                        </p>
                    </CardHeader>
                    <CardContent className="space-y-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {PROFILE_FIELDS.map((f) => (
                                <NumberItem
                                    key={f.key}
                                    label={f.label}
                                    prefix={f.prefix}
                                    suffix={f.suffix}
                                    step={f.step}
                                    max={f.max}
                                    value={profile[f.key]}
                                    placeholder={defaults?.[f.key]}
                                    onChange={setProfileKey(f.key)}
                                    hint={f.hint}
                                    testId={`pricing-${f.key}`}
                                />
                            ))}
                        </div>

                        <Separator />

                        <div>
                            <div className="font-display text-slate-900 mb-2">Story multipliers (applied to labor)</div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {STORY_KEYS.map((sk) => (
                                    <NumberItem
                                        key={sk.key}
                                        label={sk.label}
                                        suffix="x"
                                        step={0.05}
                                        max={3}
                                        value={profile.story_multipliers?.[sk.key]}
                                        placeholder={defaults?.story_multipliers?.[sk.key]}
                                        onChange={setStoryMult(sk.key)}
                                        hint={sk.hint}
                                        testId={`pricing-story-${sk.key}`}
                                    />
                                ))}
                            </div>
                        </div>

                        <Separator />

                        <div>
                            <div className="font-display text-slate-900 mb-1">Homeowner-facing range</div>
                            <p className="text-xs text-slate-500 mb-2">
                                Quotes shown to homeowners (PDF, public link, email) display this band — not a single number — to protect your credibility if the on-site quote differs.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {RANGE_FIELDS.map((f) => (
                                    <NumberItem
                                        key={f.key}
                                        label={f.label}
                                        step={f.step}
                                        min={f.min}
                                        max={f.max}
                                        value={profile[f.key]}
                                        placeholder={defaults?.[f.key]}
                                        onChange={setProfileKey(f.key)}
                                        hint={f.hint}
                                        testId={`pricing-${f.key}`}
                                    />
                                ))}
                            </div>
                            <div className="mt-3 text-xs text-slate-500 flex items-center gap-1">
                                <DollarSign className="h-3.5 w-3.5" />
                                Example: $15,000 exact → homeowner sees{' '}
                                <b className="text-slate-700 mx-1">
                                    ${Math.round(15000 * (profile.range_low_pct ?? 0.95)).toLocaleString()} – ${Math.round(15000 * (profile.range_high_pct ?? 1.10)).toLocaleString()}
                                </b>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}
        </>
    );
}

function Item({ label, v, onChange, className = '', testId }) {
    return (
        <div className={className}>
            <Label>{label}</Label>
            <Input value={v ?? ''} onChange={onChange} data-testid={testId} />
        </div>
    );
}

function NumberItem({ label, value, placeholder, onChange, prefix, suffix, step = 1, min, max, hint, testId, className = '' }) {
    return (
        <div className={className}>
            <Label>{label}</Label>
            <div className="relative">
                {prefix && (
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none">{prefix}</span>
                )}
                <Input
                    type="number"
                    inputMode="decimal"
                    step={step}
                    min={min}
                    max={max}
                    value={value ?? ''}
                    placeholder={placeholder !== undefined && placeholder !== null ? String(placeholder) : ''}
                    onChange={onChange}
                    data-testid={testId}
                    className={`${prefix ? 'pl-6' : ''} ${suffix ? 'pr-7' : ''}`}
                />
                {suffix && (
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none">{suffix}</span>
                )}
            </div>
            {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
        </div>
    );
}
